import {
  createContext,
  ReactNode,
  useMemo,
  useEffect,
  useReducer,
  useContext,
  Dispatch,
} from 'react';

import { colors as coreColors, fonts, shape } from '@EVOCORP/corechat-theme';
import CssBaseline from '@material-ui/core/CssBaseline';
import {
  responsiveFontSizes,
  ThemeProvider as MuiThemeProvider,
  unstable_createMuiStrictModeTheme as createStrictModeTheme,
} from '@material-ui/core/styles';

import { getCookie, setCookie } from 'services/cookies';

const colors = {
  light: {
    ...coreColors.light,
    channelBadges: {
      whatsapp: '#00921A',
      whatsappOfficial: '#00921A',
      instagram: '#610099',
      messenger: '#0866FF',
      telegram: '#7BACFC',
      unknown: '#ED6C02',
      offline: '#E93F66',
    },
  },
  dark: {
    ...coreColors.dark,
    channelBadges: {
      whatsapp: '#25D366',
      whatsappOfficial: '#25D366',
      instagram: '#C78CFF',
      messenger: '#64B5F6',
      telegram: '#7BACFC',
      unknown: '#FFA726',
      offline: '#FF809B',
    },
  },
};

type ThemeProviderProps = {
  children: ReactNode;
};

type ThemePaletteType = 'light' | 'dark';

type ACTIONTYPE = {
  type: 'CHANGE';
  payload: {
    paletteType: ThemePaletteType;
    fontSize?: number;
  };
};

type ThemeState = {
  paletteType: ThemePaletteType;
  borderRadius: number;
  fontSize: number;
};

const initialState = {
  paletteType: 'light',
  fontSize: 14,
} as ThemeState;

function reducer(state: typeof initialState, action: ACTIONTYPE) {
  switch (action.type) {
    case 'CHANGE':
      return {
        ...state,
        paletteType: action.payload.paletteType || state.paletteType,
        fontSize: action.payload?.fontSize || state.fontSize,
      };
    default:
      throw new Error(`Unrecognized type ${action.type}`);
  }
}

export interface ThemeContextInterface {
  state: ThemeState;
  dispatch: Dispatch<ACTIONTYPE>;
}

export const ThemeContext = createContext({} as ThemeContextInterface);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { paletteType, fontSize } = state;

  const theme = useMemo(() => {
    let nextTheme = createStrictModeTheme({
      appDrawer: {
        minWidth: 592,
      },
      overrides: {
        MuiCssBaseline: {
          '@global': {
            '*': {
              outline: 0,
              scrollbarWidth: 'thin',
              scrollbarColor: `#C1C1C1 transparent`,
            },
            'html, body, #root': {
              height: '100%',
              overflow: 'hidden',
            },
            '::-webkit-scrollbar': {
              width: 6,
            },
            '::-webkit-scrollbar-thumb': {
              borderRadius: 6,
              backgroundColor: '#C1C1C1',
            },
            '::-webkit-scrollbar-track': {
              backgroundColor: colors[paletteType].background.default,
            },
            '.MuiAutocomplete-option': {
              fontSize: `${fontSize}px !important`,
            },
            '.MuiAutocomplete-listbox': {
              fontSize: `${fontSize}px`,
            },
            '.MuiAutocomplete-noOptions': {
              fontSize: `${fontSize}px`,
            },
            '.MuiAutocomplete-tag': {
              fontSize: `${fontSize}px`,
              height: 'auto',
              '& .MuiChip-label': {
                fontSize: `${fontSize}px`,
              },
              '& .MuiChip-deleteIcon': {
                fontSize: `${fontSize}px`,
              },
            },
            '.search-highlight': {
              backgroundColor: '#ffeb3b',
              color: '#000',
              padding: '0 2px',
              borderRadius: '2px',
            },
            '.tippy-box[data-theme~="mentions"]': {
              backgroundColor: 'transparent !important',
              boxShadow: 'none !important',
            },
            '.tippy-box[data-theme~="mentions"] .tippy-content': {
              padding: '0 !important',
            },
            '.MuiInputAdornment-root .MuiAvatar-root, .MuiAutocomplete-option .MuiAvatar-root, .MuiMenuItem-root .MuiAvatar-root':
              {
                width: `${Math.round(fontSize * 2)}px !important`,
                height: `${Math.round(fontSize * 2)}px !important`,
                minWidth: 'unset !important',
                minHeight: 'unset !important',
              },
          },
        },
        MuiMenu: {
          paper: {
            padding: '0px 8px',
          },
        },
        MuiMenuItem: {
          root: {
            padding: '4px 8px',
            fontSize: `${fontSize}px`,
          },
        },
        MuiAvatar: {
          root: {
            width: Math.round(fontSize * 2.66),
            height: Math.round(fontSize * 2.66),
            flexShrink: 0,
          },
        },
        MuiDialogActions: {
          root: {
            marginTop: '16px',
            padding: '16px 24px',
            backgroundColor: paletteType === 'dark' ? '#2f3136' : '#f6f6f7',
          },
        },
        MuiListItem: {
          button: {
            padding: '4px 8px',
            borderRadius: shape.borderRadius,
            '&:hover': {
              borderRadius: shape.borderRadius,
            },
          },
        },
        MuiFormLabel: {
          root: {
            fontSize: `${fontSize}px`,
          },
        },
      },
      palette: {
        type: paletteType,
        background: {
          paper: colors[paletteType].background.paper,
          default: colors[paletteType].background.default,
          tertiary: colors[paletteType].background.tertiary,
        },
        divider: colors[paletteType].divider,
        error: {
          main: colors[paletteType].error.main,
        },
        primary: {
          main: colors[paletteType].primary.main,
        },
        secondary: {
          main: colors[paletteType].secondary.main,
          '500': '#D6DBE9',
        },
        success: {
          main: colors[paletteType].success.main,
        },
        warning: {
          main: colors[paletteType].warning.main,
        },
        text: {
          primary: colors[paletteType].text.primary,
          secondary: colors[paletteType].text.secondary,
          disabled: colors[paletteType].text.disabled,
        },
        channelBadges: colors[paletteType].channelBadges,
      },
      props: {
        MuiButton: {
          disableElevation: true,
        },
        MuiDialog: {
          disableRestoreFocus: true,
        },
        MuiTab: {
          disableRipple: true,
        },
        MuiTabs: {
          indicatorColor: 'primary',
        },
        MuiInputBase: {
          style: {
            fontSize: `${fontSize}px`,
          },
        },
        MuiInputLabel: {
          style: {
            fontSize: `${fontSize}px`,
          },
        },
        MuiMenuItem: {
          style: {
            fontSize: `${fontSize}px`,
          },
        },
        MuiTextField: {
          size: 'small',
        },
        MuiSwitch: {
          size: 'small',
        },
      },
      typography: {
        ...fonts.poppins,
        fontSize,
        button: {
          textTransform: 'none',
        },
      },
      shape: {
        borderRadius: shape.borderRadius,
      },
    });

    nextTheme = responsiveFontSizes(nextTheme);

    return nextTheme;
  }, [fontSize, paletteType]);

  useEffect(() => {
    const nextType = getCookie('paletteTypeChat') as ThemePaletteType;
    const nextFontSize = Number(getCookie('fontSize')) || 14;

    dispatch({
      type: 'CHANGE',
      payload: {
        paletteType: nextType || 'light',
        fontSize: nextFontSize,
      },
    });
  }, []);

  // persist paletteType
  useEffect(() => {
    setCookie('paletteTypeChat', paletteType);
  }, [paletteType]);

  // persist fontSize
  useEffect(() => {
    setCookie('fontSize', String(fontSize));
  }, [fontSize]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <ThemeContext.Provider value={{ state, dispatch }}>
        {children}
      </ThemeContext.Provider>
    </MuiThemeProvider>
  );
}
